import { useState, useEffect } from "react";
import { CloudArrowUpIcon, DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline";
import { showError } from "../../utils/notificationHelper";

const FileUploader = ({
  label,
  inputId,
  initialFiles = [],
  onFilesChange,
  multiple = true,
  mode = "create",
  size = false,
}) => {
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!initialFiles) return;

    const normalized = initialFiles
      .filter(f => f && (f instanceof File || f.url || f.public_file?.url))
      .map((f) => {

        if (f.type) {
          return {
            ...f,
            // Ensure existing entries have sane defaults
            size: f.size ?? f.file?.size ?? 0,
            progress: f.progress ?? 100,
            remove: f.remove ?? 0,
            _deleted: f._deleted ?? false,
          };
        }

        if (f instanceof File) {
          return {
            type: "new",
            file: f,
            name: f.name,
            size: f.size,
            url: URL.createObjectURL(f),
            progress: 0,
            remove: 0,
            _deleted: false,
          };
        }

        const url = f.url || f.file_url || f.public_file?.url || f.path || null;
        return {
          type: "existing",
          id: f.id ?? null,
          file: null,
          name: f.name || f.filename || f.public_file?.name || "file",
          size: f.size ?? f.file_size ?? f.public_file?.size ?? 0,
          url,
          progress: 100,
          remove: 0,
          _deleted: false,
        };
      });

    setFiles(normalized);
  }, [initialFiles]);

  const emitChange = (updated) => {
    setFiles(updated);
    onFilesChange?.(updated);
  };

  const handleFilesAdded = (fileList) => {
    try {
      if (!fileList || fileList.length === 0) {
        showError("No files selected. Please choose valid files.");
        return;
      }

      // Validate file types and sizes
      const validFiles = [];
      const invalidFiles = [];

      Array.from(fileList).forEach((file) => {
        // Check file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
          invalidFiles.push(`${file.name} (file too large, max 10MB)`);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        showError(`Some files could not be processed:\n${invalidFiles.join('\n')}`);
      }

      if (validFiles.length === 0) {
        return;
      }

      const newFiles = validFiles.map((f) => ({
        type: "new",
        file: f,
        name: f.name,
        size: f.size,
        url: URL.createObjectURL(f),
        progress: 0,
      }));

      const visibleFiles = files.filter(f => !f._deleted);
      const updated = multiple ? [...files, ...newFiles] : [...newFiles].slice(0, 1);

      emitChange(updated);

      newFiles.forEach((_, idx) => simulateUpload(visibleFiles.length + idx));
    } catch (error) {
      console.error('Error adding files:', error);
      showError('Failed to process files. Please try again.');
    }
  };

  const handleRemove = (index) => {
    try {
      if (index < 0 || index >= files.length) {
        showError('Invalid file selection. Please try again.');
        return;
      }

      const updated = [...files];
      const target = updated[index];

      if (!target) {
        showError('File not found. Please refresh and try again.');
        return;
      }

      if (target.type === "new") {
        updated.splice(index, 1);
      } else if (target.type === "existing") {
        updated[index] = {
          ...target,
          remove: 1,
          _deleted: true,
        };
      }

      emitChange(updated);
    } catch (error) {
      console.error('Error removing file:', error);
      showError('Failed to remove file. Please try again.');
    }
  };

  const handleDrop = (e) => {
    try {
      e.preventDefault();
      const droppedFiles = e.dataTransfer.files;
      
      if (!droppedFiles || droppedFiles.length === 0) {
        showError('No files were dropped. Please try again.');
        return;
      }
      
      handleFilesAdded(Array.from(droppedFiles));
    } catch (error) {
      console.error('Error handling drop:', error);
      showError('Failed to process dropped files. Please try again.');
    }
  };

  const handleDragOver = (e) => {
    try {
      e.preventDefault();
    } catch (error) {
      console.error('Error handling drag over:', error);
    }
  };

  const handleFilesClick = () => {
    try {
      document.getElementById(inputId)?.click();
    } catch (error) {
      console.error('Error handling file click:', error);
      showError('Failed to open file dialog. Please try again.');
    }
  };

  const formatFileSize = (size) => {
    if (size == null) return "—";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  const simulateUpload = (fileIndex) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.floor(Math.random() * 15) + 5;
      setFiles((prev) => {
        const updated = [...prev];
        if (updated[fileIndex]) {
          updated[fileIndex] = {
            ...updated[fileIndex],
            progress: Math.min(progress, 100),
          };
        }
        return updated;
      });
      if (progress >= 100) clearInterval(interval);
    }, 300);
  };

  const showUploader = mode === "create" || mode === "edit" || mode === "renewal";
  const visibleFiles = files.filter(f => !f._deleted);
  const allowUpload = multiple || visibleFiles.length === 0;

  return (
    <div className="col-span-1 md:col-span-2 w-full">
      {!(mode === "view" && visibleFiles.length === 0) && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <div className="space-y-3">
        {visibleFiles.map((file, index) => (
          <div key={index} className="border rounded-lg bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  {file?.url ? (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 truncate hover:underline block"
                    >
                      {file.name || "file"}
                    </a>
                  ) : (
                    <span className="text-sm font-medium text-gray-700 truncate block">
                      {file?.name || "file"}
                    </span>
                  )}
                  <div className="flex items-center text-xs text-gray-500 mt-1">
                    <span>
                      {file.progress === 100
                        ? formatFileSize(file.size)
                        : `${formatFileSize(Math.round((file.size * file.progress) / 100))} / ${formatFileSize(file.size)}`}
                    </span>
                    <span className="mx-2">•</span>
                    <span
                      className={`font-medium ${file.progress === 100 ? "text-green-600" : "text-blue-600"}`}
                    >
                      {file.progress === 100 ? "Completed" : "Uploading..."}
                    </span>
                  </div>
                </div>
              </div>
              {showUploader && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                  className="ml-4 p-2 text-gray-400 hover:text-red-500 rounded-full"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              )}
            </div>

            {file.progress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-1 mt-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${file.progress}%` }}
                />
              </div>
            )}
          </div>
        ))}

        {showUploader && allowUpload && (
          <div
            className="w-full border-2 border-dashed border-gray-200 bg-gray-50 rounded-lg cursor-pointer hover:border-blue-300 transition duration-150 text-center"
            onClick={handleFilesClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {size === true ? (
              <div className="px-3 py-2 text-center flex items-center">
                <CloudArrowUpIcon className="w-5 h-5 text-gray-400" />
                <p className="text-xs text-gray-500 mb-0 ms-3">
                  Drag and Drop images or any file or{" "}
                  <span className="text-blue-600 font-medium hover:underline">
                    browse files
                  </span>{" "}
                  on your computer
                </p>
              </div>
            ) : (
              <div className="p-10 text-center">
                <CloudArrowUpIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-700">
                  Drag and Drop <span className="font-semibold">{label}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  or{" "}
                  <span className="text-blue-600 font-medium hover:underline">
                    browse files
                  </span>{" "}
                  on your computer
                </p>
              </div>
            )}
            <input
              type="file"
              id={inputId}
              className="hidden"
              multiple={multiple}
              onChange={(e) => handleFilesAdded(Array.from(e.target.files))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;